import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  currencyFormatter,
  ILateFeeReport,
  ILateFeeReportRow,
  SavedFilterType,
  toDecimal,
} from "shared";
import { toast } from "react-toastify";
import {
  ExportCsv,
  FilterPanelTrigger,
  GridColDef,
  GridCsvExportOptions,
  GridFilterListIcon,
  GridFilterModel,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { Download } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { Tooltip } from "@mui/material";

import { useApiRequest } from "../../../hooks/useApiRequest";
import { MultiOrgSelector } from "../MultiOrgSelector";
import { commonConfig, getTogglableColumns } from "../../../lib/datagrid";
import { ApiSavedFilterPanel } from "../../Common/Datagrid/ApiSavedFilterPanel";
import { SaveFilterButton } from "../../Common/Datagrid/SaveFilterButton";
import { DatagridCard } from "../../Common/Datagrid/DatagridCard";
import { useApplySavedFilterFromUrl } from "../../../hooks/useApplySavedFilterFromUrl";
import { useOrganisationsIncludeDisconnected } from "../../../hooks/useOrganisationsIncludeDisconnected";
import {
  buildOrganisationsFilterColumn,
  getReportFilterValue,
  ReportFilterField,
  reportFilterColumnVisibility,
  reportFilterFieldList,
  withReportFilterValue,
} from "../reportGridFilters";

const baseColumns: GridColDef<ILateFeeReportRow>[] = [
  {
    field: `organisationName`,
    headerName: `Entity Name`,
    minWidth: 130,
  },
  {
    field: `contactName`,
    headerName: `Contact Name`,
    minWidth: 130,
  },
  {
    field: `dateStr`,
    headerName: `Date`,
    filterable: false,
  },
  {
    field: `feeInCents`,
    headerName: `Amount`,
    valueGetter: (value, row) => toDecimal(row.feeInCents),
    valueFormatter: (value, row) => {
      const currency = row.currency;

      return currencyFormatter(currency)(value);
    },
    filterable: false,
  },
  {
    type: `boolean`,
    field: `fullyPaid`,
    headerName: `Fully Paid?`,
  },
  {
    field: `type`,
    headerName: `Type`,
    valueGetter: value => {
      if (value === `interest_charge`) {
        return `Interest Charge`;
      }

      if (value === `invoice_create`) {
        return `New Invoice`;
      }

      if (value === `invoice_update`) {
        return `Update Invoice`;
      }

      return `Unknown Type`;
    },
    filterable: false,
  },
  {
    field: `resultInvoice`,
    headerName: `Result Invoice`,
    valueGetter: (value, row) => row.resultInvoice.number || `-`,
    renderCell: ({ row }) => (
      <span>
        <Link to={ `/${row.organisationId}/invoices/${row.resultInvoice.id}` }>
          <span className={ `text-blue-600 underline whitespace-wrap` }>
            { row.resultInvoice.number || `-` }
          </span>
        </Link>
      </span>
    ),
    filterable: false,
  },
  {
    field: `sourceInvoice`,
    headerName: `Source Invoice`,
    description: `Not applicable for statement late fees`,
    valueGetter: (value, row) => row.originalInvoice?.number || `-`,
    renderCell: ({ row }) => {
      if (!row.originalInvoice) {
        return null;
      }

      return (
        <span>
          <Link to={ `/${row.organisationId}/invoices/${row.originalInvoice.id}` }>
            <span className={ `text-blue-600 underline whitespace-wrap` }>
              { row.originalInvoice.number || `-` }
            </span>
          </Link>
        </span>
      );
    },
    filterable: false,
  },
];

export function LateFeeReport() {
  const apiRequest = useApiRequest();
  const apiRef = useGridApiRef();
  const [reportResponse, setReportResponse] = useState<ILateFeeReport>({
    rows: [],
  });
  const [reportLoading, setReportLoading] = useState(false);
  // The org selection lives in the grid filter model so it is captured by
  // saved filter presets
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const { data: allOrganisations } = useOrganisationsIncludeDisconnected();

  const orgOptions = useMemo(() => {
    return allOrganisations.map(org => ({
      value: org.id,
      label: org.legalName,
    }));
  }, [allOrganisations]);

  const selectedOrgs = useMemo(() => {
    const value = getReportFilterValue(filterModel, ReportFilterField.organisations);

    return Array.isArray(value) ? value as string[] : [];
  }, [filterModel]);

  // Serialised so the fetch effect only re-runs when the org selection
  // changes, not on unrelated column filter edits
  const reportQueryKey = JSON.stringify({ orgs: selectedOrgs });

  useEffect(() => {
    const params = JSON.parse(reportQueryKey) as { orgs: string[] };

    async function fetchReport() {
      try {
        setReportLoading(true);

        const response = await apiRequest
          .get<ILateFeeReport>(`/reports/latefee`, {
            params,
          });

        setReportResponse(response.data);
        setReportLoading(false);
      }
      catch (err) {
        setReportLoading(false);
        console.error(`Error fetching late fee report`, err);
        toast.error(`Could not fetch late fee report. ${err?.response?.data?.toString() || ``}`);
      }
    }

    if (params.orgs.length > 0) {
      fetchReport();
    }

    if (params.orgs.length === 0) {
      setReportResponse({
        rows: [],
      });

      setReportLoading(false);
    }
  }, [reportQueryKey, apiRequest]);

  useApplySavedFilterFromUrl(apiRef, SavedFilterType.ReportLateFee, {
    defaultColumnVisibilityModel: reportFilterColumnVisibility,
  });

  function setSelectedOrgs(orgs: string[]) {
    setFilterModel(model => withReportFilterValue(model, ReportFilterField.organisations, `isAnyOf`, orgs));
  }

  const columns: GridColDef<ILateFeeReportRow>[] = useMemo(() => {
    return [
      ...baseColumns,
      // Hidden column carrying the org selection in the filter model
      buildOrganisationsFilterColumn(orgOptions),
    ];
  }, [orgOptions]);

  const viewportHeight = window.innerHeight;
  const tableHeight = viewportHeight
    - 180; // px for the header

  return (
    <div className={ `flex flex-col h-full` }
      style={ { height: tableHeight } }>
      <div className={ `flex gap-4 mb-4 items-end` }>
        <div className={ `w-1/3` }>
          <MultiOrgSelector
            onChange={ setSelectedOrgs }
            selected={ selectedOrgs }
          />
        </div>
      </div>
      <DatagridCard className={ `flex-1` }>
        <DataGridPro
          apiRef={ apiRef }
          columns={ columns }
          rows={ reportResponse.rows }
          loading={ reportLoading }
          getRowId={ row => row.id }
          rowHeight={ 38 }
          filterModel={ filterModel }
          onFilterModelChange={ setFilterModel }
          initialState={ {
            columns: {
              columnVisibilityModel: reportFilterColumnVisibility,
            },
          } }
          slots={ { toolbar: CustomToolbar, filterPanel: ApiSavedFilterPanel } }
          showToolbar
          slotProps={ {
            columnsManagement: {
              getTogglableColumns: columns => getTogglableColumns(columns, reportFilterFieldList),
            },
            filterPanel: {
              savedFilterType: SavedFilterType.ReportLateFee,
            },
          } }
          sx={ {
            ...commonConfig.headerStyle,
          } }
        />
      </DatagridCard>
    </div>
  );
}

function CustomToolbar() {
  const csvOptions: GridCsvExportOptions = {
    fileName: `late_fee_report`,
  };

  return (
    <Toolbar>
      <Tooltip title={ `Filters` }>
        <FilterPanelTrigger render={ <ToolbarButton /> }>
          <GridFilterListIcon fontSize={ `medium` } />
        </FilterPanelTrigger>
      </Tooltip>
      <SaveFilterButton type={ SavedFilterType.ReportLateFee } />
      <div className={ `flex justify-end w-full` }>
        <ExportCsv
          render={ <ToolbarButton /> }
          options={ csvOptions }
        >
          <Download fontSize={ `medium` }/>
        </ExportCsv>
      </div>
    </Toolbar>
  );
}
